import { aspectRatio, type NormalizedPoint, type NormalizedRect } from '@photovalidator/core';

/**
 * Static stage layout.
 *
 * The validator shows the uploaded photo **exactly as it is**: no zoom, no pan,
 * no rotation, no cropping. The only transformation is fitting the photo into
 * the available area while preserving its aspect ratio — a viewer concern, not
 * an editing one.
 *
 * Because there is no user transform, overlay geometry (normalized 0…1) maps to
 * the screen with one simple rectangle, and the SVG overlay lives in the same
 * non-transformed element as the canvas, so markup cannot drift from the photo.
 */

export interface Size {
  width: number;
  height: number;
}

export interface ScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

/** Contain-fit size of an image inside a box, preserving the aspect ratio. */
export const fitContain = (image: Size, box: Size): Size => {
  if (image.width <= 0 || image.height <= 0 || box.width <= 0 || box.height <= 0) {
    return { width: 0, height: 0 };
  }
  const scale = Math.min(box.width / image.width, box.height / image.height);
  return { width: image.width * scale, height: image.height * scale };
};

/**
 * Layout rectangle of the photo inside the stage, relative to the stage.
 * Always centred, never scaled by the user.
 */
export const baseRect = (stage: Size, image: Size): ScreenRect => {
  const fitted = fitContain(image, stage);
  return {
    left: (stage.width - fitted.width) / 2,
    top: (stage.height - fitted.height) / 2,
    width: fitted.width,
    height: fitted.height,
  };
};

/** Normalized image point → coordinates relative to the stage. */
export const normalizedToScreen = (point: NormalizedPoint, rect: ScreenRect): Point => ({
  x: rect.left + point.x * rect.width,
  y: rect.top + point.y * rect.height,
});

/** Stage coordinates → normalized image point (used for tap hit-testing). */
export const screenToNormalized = (point: Point, rect: ScreenRect): NormalizedPoint => ({
  x: rect.width > 0 ? (point.x - rect.left) / rect.width : 0,
  y: rect.height > 0 ? (point.y - rect.top) / rect.height : 0,
});

/** Screen-space corners and centre of a normalized rect (markers, hit tests). */
export const normalizedRectToScreen = (
  rect: NormalizedRect,
  base: ScreenRect,
): { points: [Point, Point, Point, Point]; centre: Point } => ({
  points: [
    normalizedToScreen({ x: rect.x, y: rect.y }, base),
    normalizedToScreen({ x: rect.x + rect.width, y: rect.y }, base),
    normalizedToScreen({ x: rect.x + rect.width, y: rect.y + rect.height }, base),
    normalizedToScreen({ x: rect.x, y: rect.y + rect.height }, base),
  ],
  centre: normalizedToScreen({ x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 }, base),
});

export const imageAspect = (size: Size): number => aspectRatio(size.width, size.height);
